import  { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Stack,
  Link,
  useMediaQuery,
  useTheme,
  Tooltip,
  Chip,
  List,
  ListItem,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  LocalShipping as LocalShippingIcon,
  Cached as CachedIcon,
  Copyright as CopyrightIcon,
  Verified as VerifiedIcon,
  AccessTime as AccessTimeIcon,
  CreditCard as CreditCardIcon,
  SupportAgent as SupportAgentIcon,
} from "@mui/icons-material";

const Footer = () => {
  const theme = useTheme();

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Contact Us", path: "/contact" },
    { name: "FAQs", path: "/faq" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Return Policy", path: "/return-policy" },
  ];

  const shopLinks = [
    { name: "New Arrivals", path: "/products?sort=newest" },
    { name: "Best Sellers", path: "/products?sort=bestselling" },
    { name: "Trending Now", path: "/products?sort=trending" },
    { name: "Special Offers", path: "/products?discount=true" },
    { name: "Flash Sales", path: "/flash-sales" },
    { name: "Gift Cards", path: "/gift-cards" },
  ];

  const categoryLinks = [
    { name: "Electronics", path: "/category/electronics" },
    { name: "Fashion", path: "/category/fashion" },
    { name: "Home & Living", path: "/category/home-furniture" },
    { name: "Books & Media", path: "/category/books" },
    { name: "Sports & Outdoors", path: "/category/sports" },
    { name: "Toys & Games", path: "/category/toys" },
  ];

  const features = [
    { icon: <LocalShippingIcon />, title: "Free Shipping", desc: "On orders over ₹999" },
    { icon: <CachedIcon />, title: "Easy Returns", desc: "30 days return policy" },
    { icon: <SecurityIcon />, title: "Secure Payments", desc: "100% secure transactions" },
    { icon: <SupportAgentIcon />, title: "24/7 Support", desc: "Dedicated customer service" },
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, name: "Facebook", color: "#1877f2", url: "https://facebook.com" },
    { icon: <InstagramIcon />, name: "Instagram", color: "#e4405f", url: "https://instagram.com" },
    { icon: <TwitterIcon />, name: "Twitter", color: "#1da1f2", url: "https://twitter.com" },
    { icon: <LinkedInIcon />, name: "LinkedIn", color: "#0077b5", url: "https://linkedin.com" },
    { icon: <YouTubeIcon />, name: "YouTube", color: "#ff0000", url: "https://youtube.com" },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 3000);
    }
  };



  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#ffffff",
        color: "#000000",
        mt: "auto",
        position: "relative",
      }}
    >
      {/* Features Section */}
      <Box
        sx={{
          py: 4,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          background: "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    textAlign: "center",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      bgcolor: "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: "#ff6b6b",
                      "& svg": { fontSize: 36 },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: "#000000" }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Main Footer Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} justifyContent="center">
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src="images/logo.png" // 👉 apna logo yaha daalo
                  alt="Shopmart Logo"
                  sx={{
                    height: { xs: 50, md: 60 },
                    width: "auto",
                  }}
                />
              </Typography>
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                label="Trusted Online Store"
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: "rgba(255,107,107,0.1)",
                  color: "#ff6b6b",
                  border: "1px solid rgba(255,107,107,0.3)",
                }}
              />
            </Box>

            <Stack spacing={2} alignItems="center">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <LocationIcon sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
                  123 Shopping Street, Mumbai - 400001
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <PhoneIcon sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
                  +91 12345 67890
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <EmailIcon sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
                  support@tympayservices.com
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <AccessTimeIcon sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
                  Mon-Sat: 10 AM - 8 PM
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: "1.1rem",
                  position: "relative",
                  display: "inline-block",
                  color: "#000000",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "3px",
                    background: "#ff6b6b",
                    borderRadius: "2px",
                  },
                }}
              >
                Quick Links
              </Typography>
            </Box>
            <List sx={{ p: 0, textAlign: "center" }}>
              {quickLinks.map((link) => (
                <ListItem
                  key={link.name}
                  disablePadding
                  sx={{
                    mb: 1.5,
                    justifyContent: "center",
                    "&:hover": {
                      transform: "translateX(5px)",
                    },
                    transition: "transform 0.3s ease",
                  }}
                >
                  <Link
                    href={link.path}
                    underline="none"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      "&:hover": { color: "#ff6b6b" },
                    }}
                  >
                    {link.name}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Shop Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: "1.1rem",
                  position: "relative",
                  display: "inline-block",
                  color: "#000000",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "3px",
                    background: "#ff6b6b",
                    borderRadius: "2px",
                  },
                }}
              >
                Shop
              </Typography>
            </Box>
            <List sx={{ p: 0, textAlign: "center" }}>
              {shopLinks.map((link) => (
                <ListItem
                  key={link.name}
                  disablePadding
                  sx={{
                    mb: 1.5,
                    justifyContent: "center",
                    "&:hover": {
                      transform: "translateX(5px)",
                    },
                    transition: "transform 0.3s ease",
                  }}
                >
                  <Link
                    href={link.path}
                    underline="none"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      "&:hover": { color: "#ff6b6b" },
                    }}
                  >
                    {link.name}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Categories */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: "1.1rem",
                  position: "relative",
                  display: "inline-block",
                  color: "#000000",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "3px",
                    background: "#ff6b6b",
                    borderRadius: "2px",
                  },
                }}
              >
                Categories
              </Typography>
            </Box>
            <List sx={{ p: 0, textAlign: "center" }}>
              {categoryLinks.map((link) => (
                <ListItem
                  key={link.name}
                  disablePadding
                  sx={{
                    mb: 1.5,
                    justifyContent: "center",
                    "&:hover": {
                      transform: "translateX(5px)",
                    },
                    transition: "transform 0.3s ease",
                  }}
                >
                  <Link
                    href={link.path}
                    underline="none"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      "&:hover": { color: "#ff6b6b" },
                    }}
                  >
                    {link.name}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: "1.1rem",
                  position: "relative",
                  display: "inline-block",
                  color: "#000000",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "3px",
                    background: "#ff6b6b",
                    borderRadius: "2px",
                  },
                }}
              >
                Newsletter
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mb: 2.5, fontSize: "0.875rem", textAlign: "center" }}>
              Get 10% off your first purchase!
            </Typography>
            <Box component="form" onSubmit={handleNewsletterSubmit}>
              <TextField
                size="small"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.03)",
                    color: "#000000",
                    "& fieldset": {
                      borderColor: "rgba(0,0,0,0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,107,107,0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ff6b6b",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "#000000",
                    "&::placeholder": {
                      color: "rgba(0,0,0,0.5)",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="submit"
                        sx={{
                          color: "#ff6b6b",
                          "&:hover": { transform: "scale(1.1)" },
                          transition: "transform 0.3s ease",
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {subscribed && (
              <Typography variant="caption" sx={{ color: "#4caf50", display: "block", mb: 2, textAlign: "center" }}>
                ✓ Subscribed successfully!
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)", display: "block", mb: 2, textAlign: "center" }}>
              Follow us on social media
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              {socialLinks.map((social, index) => (
                <Tooltip key={index} title={social.name} arrow>
                  <IconButton
                    component="a"
                    href={social.url}
                    target="_blank"
                    sx={{
                      bgcolor: "rgba(0,0,0,0.05)",
                      color: "rgba(0,0,0,0.7)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: social.color,
                        color: "white",
                        transform: "translateY(-3px)",
                      },
                    }}
                    size="small"
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Divider sx={{ bgcolor: "rgba(0,0,0,0.1)" }} />

      {/* Bottom Bar */}
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center" justifyContent="center" textAlign="center">

            {/* Copyright */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <CopyrightIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.5)" }} />

                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)" }}>
                  {currentYear} TYM PAY SERVICES. All rights reserved.
                </Typography>

                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)" }}>
                  •
                </Typography>

                <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                  Made with ❤️ in India
                </Typography>
              </Box>
            </Grid>

            {/* Payments */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                  Secure Payments:
                </Typography>

                <Stack direction="row" spacing={1}>
                  <CreditCardIcon sx={{ color: "rgba(0,0,0,0.6)", fontSize: 28 }} />
                  <PaymentIcon sx={{ color: "rgba(0,0,0,0.6)", fontSize: 28 }} />
                </Stack>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;